import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiRequest } from '@/services/api'
import Spinner from '@/components/Spinner'

/**
 * Confirm email change page component
 * Page for confirming email address change via token link
 */
export default function ConfirmEmailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [newEmail, setNewEmail] = useState<string | null>(null)

  // Track if confirmation has been attempted to prevent multiple API calls
  const confirmationAttemptedRef = useRef(false)

  useEffect(() => {
    // Redirect if no token
    if (!token) {
      navigate('/', { replace: true })
      return
    }

    // Prevent multiple confirmation attempts
    if (confirmationAttemptedRef.current) {
      return
    }
    confirmationAttemptedRef.current = true

    // Confirm email change immediately
    const confirmEmail = async () => {
      try {
        setIsLoading(true)
        setError(null)
        setSuccess(false)

        const response = await apiRequest<{ email: string }>({
          method: 'POST',
          url: `/users/me/email/confirm-change/${token}`,
        })

        if (!response.success) {
          setError(response.message || t('email.confirmationFailed'))
          setIsLoading(false)
          return
        }

        setNewEmail(response.data?.email || null)
        setSuccess(true)
        setIsLoading(false)

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 3000)
      } catch (err: any) {
        console.error('Email confirmation error:', err)
        setError(err.message || t('email.confirmationFailed'))
        setIsLoading(false)
      }
    }

    confirmEmail()
  }, [token, navigate, t])

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="relative w-full max-w-lg">
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-linear-to-r from-cyan-500 via-purple-500 to-red-500 rounded-2xl opacity-20 blur-2xl" />

        {/* Card */}
        <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner />
              <p className="mt-4 text-center text-slate-400 font-mono text-sm">
                {t('email.verifyingDescription')}
              </p>
            </div>
          )}

          {!isLoading && success && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-5xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-center mb-2 text-green-400">
                {t('email.success')}
              </h2>
              <p className="text-center text-slate-400 font-mono text-sm mb-6">
                {t('email.confirmationSuccess')}
              </p>
              {newEmail && (
                <p className="text-center text-sm text-slate-300 font-mono mb-6">
                  {t('email.newEmailAddress')}: <span className="text-green-400">{newEmail}</span>
                </p>
              )}
              <p className="text-center text-sm text-slate-400 font-mono">
                {t('email.redirectingToLogin')}
              </p>
              <Link
                to="/login"
                className="mt-4 px-4 py-2.5 bg-linear-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider inline-block"
              >
                {t('auth.login')}
              </Link>
            </div>
          )}

          {!isLoading && error && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-5xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-center mb-2 text-red-400">
                {t('email.confirmationFailed')}
              </h2>
              <p className="text-center text-slate-400 font-mono text-sm mb-6">
                {error}
              </p>
              <div className="flex gap-2">
                <Link
                  to="/"
                  className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
                >
                  {t('common.backHome')}
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2.5 bg-linear-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
                >
                  {t('auth.login')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

